// Новая формулировка библиотеки. Панель, а не модалка: заведение обратимо — черновик до
// утверждения наружу не уходит вовсе (правило 4).
//
// ## Черновик заводит один человек, утверждает другой
//
// Это не бюрократия, а вся защита, которая осталась после того, как текст перестали сочинять
// на ходу: фразу, которую помощник скажет от имени компании соискателю, читают двое. Права
// разведены под это же — заводит тот, кто ведёт переписку, утверждает тот, кто настраивает
// помощника. Сервер отказывает в утверждении собственного черновика, и человек должен знать
// об этом до того, как заведёт запись, а не после.
//
// ## Пустая учётная запись — это значение, а не пропуск
//
// Формулировка без записи общая для всех трёх: так и подписано. Правило 7 держится подписью
// пункта «не выбрано», а не догадкой.

import type { FormEvent } from 'react';
import { useState } from 'react';

import type { ApiError } from '@/shared/errors/api-error';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';

import type { LibraryDraftIn } from '../api/dialogs';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { useAccountsQuery, useCreatePhraseMutation, useScriptQuery } from '../queries';
import { BLANK } from '../route';

/** Заводить формулировки и вести переписку — одно право. */
const OPERATE = 'hiring.assistant.operate';

/** Потолок текста на сервере (`ЧерновикФразыIn.text`). Держим тот же и здесь. */
const ПРЕДЕЛ_ТЕКСТА = 2000;

/** Виды формулировки — `library_entry_kind`. Четыре, и они не сводятся друг к другу. */
export const PHRASE_KINDS = ['question', 'answer', 'closing', 'fallback'] as const;
export type PhraseKind = (typeof PHRASE_KINDS)[number];

export const PHRASE_KIND_TITLES: Readonly<Record<PhraseKind, string>> = {
  question: 'Вопрос',
  answer: 'Ответ',
  closing: 'Завершение',
  fallback: 'Запасная',
};

/** Название вида не отвечает на вопрос «когда её скажут». Отвечает пояснение. */
export const PHRASE_KIND_NOTES: Readonly<Record<PhraseKind, string>> = {
  question: 'Помощник задаёт её сам, когда доходит до этого шага разговора.',
  answer: 'Ответ на встречный вопрос соискателя на этом шаге.',
  closing: 'Завершает разговор: помощник говорит её, когда квалификация пройдена.',
  fallback: 'Уходит тогда, когда подходящей формулировки на шаге не нашлось.',
};

export function phraseKindTitle(код: string): string {
  return PHRASE_KIND_TITLES[код as PhraseKind] ?? код;
}

export function NewPhrasePanel({ stepCode, onClose }: { stepCode: string; onClose: () => void }) {
  const разрешено = useCan(OPERATE);
  const шаги = useScriptQuery(undefined);
  const записи = useAccountsQuery();
  const mutation = useCreatePhraseMutation();

  const шагЗадан = stepCode !== BLANK;

  const [шаг, setШаг] = useState(шагЗадан ? stepCode : NO_VALUE);
  const [вид, setВид] = useState<string>('question');
  const [текст, setТекст] = useState('');
  const [аккаунт, setАккаунт] = useState(NO_VALUE);
  const [ошибки, setОшибки] = useState<Partial<Record<'step' | 'kind' | 'text', string>>>({});
  const [заметка, setЗаметка] = useState<string | null>(null);

  const списокШагов = шаги.data?.items ?? [];
  const выбранныйШаг = списокШагов.find((строка) => строка.code === шаг);

  function завести(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!разрешено) {
      setЗаметка(
        'Заводить формулировки может тот, кто ведёт переписку с соискателями. Право выдаёт администратор системы.',
      );
      return;
    }
    if (mutation.isPending) {
      setЗаметка('Формулировка уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const найдено: Partial<Record<'step' | 'kind' | 'text', string>> = {};
    if (шаг === NO_VALUE || шаг === '') {
      найдено.step =
        'Выберите шаг разговора: формулировка живёт на шаге, и помощник берёт её именно по нему.';
    }
    if (!(PHRASE_KINDS as readonly string[]).includes(вид)) {
      найдено.kind = 'Выберите вид формулировки.';
    }
    const очищенный = текст.trim();
    if (очищенный === '') {
      найдено.text = 'Напишите текст: пустую формулировку помощнику сказать нечем.';
    } else if (очищенный.length > ПРЕДЕЛ_ТЕКСТА) {
      найдено.text = `Текст длиннее ${ПРЕДЕЛ_ТЕКСТА} знаков не сохранится. Сейчас ${очищенный.length}: сократите или разбейте на две формулировки.`;
    }

    if (Object.keys(найдено).length > 0) {
      setОшибки(найдено);
      return;
    }

    setОшибки({});
    setЗаметка(null);
    const тело: LibraryDraftIn = {
      step_code: шаг,
      kind: вид,
      text: очищенный,
      account_id: аккаунт === NO_VALUE ? null : аккаунт,
    };
    mutation.mutate(тело, { onSuccess: onClose });
  }

  return (
    <form onSubmit={завести} noValidate className='flex flex-col gap-3'>
      {!разрешено && (
        <NoPermissionNote
          что='заводить формулировки'
          кто='Их заводит рекрутёр — попросите администратора выдать право.'
        />
      )}

      {шагЗадан ? (
        <div className='flex flex-col gap-1'>
          <span className='text-paragraph-xs text-text-sub-600'>Шаг разговора</span>
          {шаги.data === undefined && шаги.isPending ? (
            <Skeleton className='h-5 w-2/3' />
          ) : выбранныйШаг !== undefined ? (
            <span className='text-paragraph-sm text-text-strong-950'>{выбранныйШаг.title}</span>
          ) : (
            // Шаг пришёл из адреса и в сценарии его нет: сервер такую запись не примет.
            // Сказать об этом до заполнения дешевле, чем отказом после.
            <Hint.Root hasError>
              Такого шага в сценарии разговора нет. Закройте панель и выберите шаг в отборе над
              списком — формулировку можно завести только на существующий.
            </Hint.Root>
          )}
        </div>
      ) : (
        <SelectField
          id='phrase-step'
          label='Шаг разговора'
          required
          value={шаг}
          onChange={setШаг}
          options={списокШагов.map((строка) => ({ value: строка.code, label: строка.title }))}
          placeholder='Выберите шаг'
          pending={шаги.isPending}
          failed={шаги.isError}
          failedError={шаги.error as ApiError | null}
          emptyNote='Сценарий разговора пуст: шагов нет, а без шага формулировке негде жить.'
          error={ошибки.step}
          hint='Помощник выбирает формулировку по шагу, до которого дошёл разговор.'
        />
      )}

      <SelectField
        id='phrase-kind'
        label='Вид формулировки'
        required
        value={вид}
        onChange={setВид}
        options={PHRASE_KINDS.map((код) => ({ value: код, label: PHRASE_KIND_TITLES[код] }))}
        placeholder='Выберите вид'
        error={ошибки.kind}
        hint={PHRASE_KIND_NOTES[вид as PhraseKind] as string | undefined}
      />

      <TextArea
        id='phrase-text'
        label='Текст формулировки'
        required
        value={текст}
        onChange={setТекст}
        rows={5}
        maxLength={ПРЕДЕЛ_ТЕКСТА}
        placeholder='Ровно так, как это скажет помощник соискателю.'
        error={ошибки.text}
        hint='Один вопрос на формулировку: три вопроса в одном сообщении получают один ответ, и две трети выясненного теряются молча.'
      />

      <SelectField
        id='phrase-account'
        label='Учётная запись площадки'
        value={аккаунт}
        onChange={setАккаунт}
        options={(записи.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.title,
        }))}
        noneLabel='Общая для всех записей'
        placeholder='Общая для всех записей'
        pending={записи.isPending}
        failed={записи.isError}
        failedError={записи.error as ApiError | null}
        emptyNote='Учётных записей площадки нет: формулировка останется общей.'
        hint={
          аккаунт === NO_VALUE
            ? 'Без записи формулировка общая: помощник может сказать её в переписке по любой из них.'
            : 'Формулировка будет доступна только в переписке по этой учётной записи.'
        }
      />

      {/* Обязательная строка: человек должен знать про два шага ДО того, как нажмёт. */}
      <Hint.Root>
        Формулировка заводится черновиком и в переписку не попадёт, пока её не утвердит другой
        человек: утвердить собственный черновик нельзя.
      </Hint.Root>

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {заметка !== null && <GateNote>{заметка}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Сохраняем…' : 'Завести черновик'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
